import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "../styles/index.css";
import { FaSpinner } from "react-icons/fa";
import MapboxMap from "./MapboxMap"; // adjust path as needed
import wellknown from 'wellknown';
import { useNavigate } from 'react-router-dom';
import { FormControl, InputLabel, Select, MenuItem, OutlinedInput, Box, Chip } from '@mui/material';
import Button from "@mui/material/Button";

const CommonForm = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  fields = [],
  title = "Form",
  readOnlyFields = [],
  dropdownOptions = {},
  customClass = "",
  isEditMode = false,
  viewOnly = false,
  geojson = null,
  mapViewOnly = false,
  isSitePage = false,
  readOnlyFieldMapping = {},
  showAddPolygonButton = false,
  showEditPolygonButton = false,
  showCloseButton = false,
  showCancelButton = false,
  showCancelButtonSitePage = false,
}) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });
  const navigate = useNavigate();

  // // Inside handleFileUpload in your form
  const MAX_FILE_SIZE_KB = 70;

  const handleFileUpload = async (e, id) => {
    const file = e.target.files[0];
    if (!file) return;

    // ✅ Check size in KB
    const fileSizeKB = file.size / 1024;
    if (fileSizeKB > MAX_FILE_SIZE_KB) {
      toast.error(`File is too large. Max ${MAX_FILE_SIZE_KB} KB allowed.`, {
        position: "top-center",
        autoClose: 3000,
      });
      e.target.value = ""; // reset input
      return;
    }

    // ✅ Convert to base64 + preview
    const base64 = await convertToBase64(file);
    const previewUrl = URL.createObjectURL(file);

    setFormData((prev) => ({
      ...prev,
      file_data: base64,
      file_type: file.type,
      file_name: file.name,
      file_preview_url: previewUrl,
    }));
  };



  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]); // Strip "data:...base64,"
      reader.onerror = reject;
    });
  };


  const ITEM_HEIGHT = 36; // Or 48 depending on your desired height
  const ITEM_PADDING_TOP = 8;

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };


  // useEffect(() => {
  //   setFormData(initialData);
  // }, [initialData]);

  useEffect(() => {
    if (initialData) {
      let filePreviewURL = initialData.file_url || null;

      if (initialData.file_data && initialData.file_type) {
        const byteCharacters = atob(initialData.file_data);
        const byteNumbers = new Array(byteCharacters.length).fill().map((_, i) =>
          byteCharacters.charCodeAt(i)
        );
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: initialData.file_type });
        filePreviewURL = URL.createObjectURL(blob);
      }

      setFormData(prev => ({
        ...prev,
        ...initialData,
        file_preview_url: filePreviewURL,
        file_name: initialData.file_name || prev.file_name,
        file_type: initialData.file_type || prev.file_type
      }));
    }
  }, [initialData]);





  const handleChange = (e) => {
    const { id, type, value } = e.target;

    if (type === "file") return;

    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    fields.forEach((field) => {
      if (field.required && !formData[field.id]) {
        newErrors[field.id] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);
      await onSave(formData);
      // ✅ Clear form data from localStorage
      localStorage.removeItem('siteFormData');
      onClose();
    } catch (error) {
      toast.error("Error saving data");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`form-container ${customClass} ${isCollapsed ? "collapsed-sidebar" : "expanded-sidebar"}`}>
      <div className={`form ${isCollapsed ? "collapsed-sidebar" : "expanded-sidebar"}`}>
        <form onSubmit={handleSubmit} className="modal-form">
          {/* <h2>{title}</h2> */}
          {/* Show only Map in Site Page View Mode */}
          {/* Show form in all other cases */}
          <div className="form-grid">
            {fields.map(({ id, label, type, required, placeholder, inputProps = {}, options = [] }) => {
              const isReadOnly = readOnlyFields.includes(id);
              // const isDropdown = dropdownOptions[id];
              const isDropdown = type === "select" || type === "multiselect" || Array.isArray(dropdownOptions[id]);


              const selectOptions = dropdownOptions[id] || inputProps.options || options || [];

              return (
                <div className="form-group edit" key={id}>
                  <label className="form-label" htmlFor={id}>
                    {label}
                  </label>


                  {isDropdown ? (
                    type === "multiselect" ? (
                      <FormControl fullWidth size="small">
                        <InputLabel id={`${id}-label`}>{label}</InputLabel>
                        <Select
                          labelId={`${id}-label`}
                          id={id}
                          multiple
                          value={formData[id] || []}
                          onChange={(e) => {
                            const selectedValues = typeof e.target.value === "string"
                              ? e.target.value.split(",")
                              : e.target.value;
                            setFormData({ ...formData, [id]: selectedValues });
                          }}
                          disabled={viewOnly}
                          input={<OutlinedInput id={`select-multichip-${id}`} label={label} />}
                          renderValue={(selected) => {
                            if (viewOnly) {
                              const selectedLabels = selected
                                .map((value) => dropdownOptions[id]?.find((opt) => opt.value === value)?.label || value)
                                .join(", ");
                              return <span style={{ color: "#666" }}>{selectedLabels}</span>;
                            }

                            return (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.25,
                                  minHeight: '30px',
                                  alignItems: 'center',
                                }}
                              >
                                {selected.map((value) => {
                                  const selectedLabel =
                                    dropdownOptions[id]?.find((opt) => opt.value === value)?.label || value;
                                  return (
                                    <Chip
                                      key={value}
                                      label={selectedLabel}
                                      size="small"
                                      sx={{ height: 22 }}
                                    />
                                  );
                                })}
                              </Box>
                            );
                          }}
                          MenuProps={MenuProps}
                          sx={{
                            backgroundColor: viewOnly ? '#eef1f6;' : 'white',
                            '.MuiSelect-select': {
                              paddingTop: '4px',
                              paddingBottom: '4px',
                              minHeight: '36px',
                              color: viewOnly ? '#666' : 'inherit',
                            },
                          }}
                        >
                          {dropdownOptions[id]?.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <select
                        id={id}
                        name={id}
                        value={formData[id] || ""}
                        onChange={handleChange}
                        disabled={isReadOnly || viewOnly}
                        className={`form-select ${(isReadOnly || viewOnly) ? "readonly" : ""} ${viewOnly ? "view-only" : ""}`}
                      >
                        <option value="">Select {label}</option>
                        {selectOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )
                  ) : (
                    <>
                      {type === "file" ? (
                        <>
                          <div className="custom-file-input-wrapper">
                            <label htmlFor={`file-${id}`} className={`custom-file-box ${viewOnly ? "readonly" : ""}`}>
                              {formData.file_preview_url && formData.file_name ? (
                                <a
                                  href="#"
                                  className="file-preview-box"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    window.open(formData.file_preview_url, "_blank");
                                  }}
                                >
                                  {formData.file_type?.includes("image") ? (
                                    <img src={formData.file_preview_url} alt="Preview" className="file-thumbnail" />
                                  ) : formData.file_type === "application/pdf" ? (
                                    <img src="/assets/images/pdf_icon.png" alt="PDF Icon" className="file-icon" />
                                  ) : (
                                    <img src="/assets/images/document_icon.png" alt="Doc Icon" className="file-icon" />
                                  )}
                                  <span className="file-name">{formData.file_name}</span>
                                </a>

                              ) : (
                                <span className="file-placeholder">Choose File</span>
                              )}
                            </label>

                            <input
                              id={`file-${id}`}
                              type="file"
                              name={id}
                              onChange={(e) => handleFileUpload(e, id)}
                              className="hidden-file-input"
                              disabled={viewOnly}
                            />
                          </div>
                        </>
                      ) : (
                        <input
                          id={id}
                          type={(id === "password" || id === "confirmPassword") && formData[id] === "********" ? "text" : type}
                          name={id}
                          value={
                            id === "geometry" && typeof formData[id] === "object"
                              ? wellknown.stringify(formData[id])
                              : formData[id] || ""
                          }
                          onChange={handleChange}
                          placeholder={placeholder || `Enter ${label}`}
                          readOnly={isReadOnly || viewOnly || inputProps.readOnly}
                          className={`${(isReadOnly || viewOnly) ? "readonly" : ""} ${viewOnly ? "view-only" : ""}`}
                        />
                      )}
                    </>
                  )}
                  {errors[id] && <span className="error-message">{errors[id]}</span>}
                </div>
              );
            })}
          </div>

          {
            geojson && !(isSitePage && viewOnly) && (
              <div className="form-group full-width-map">
                <span className="form-label-site">Site View</span>
                <MapboxMap
                  geojson={{
                    type: "FeatureCollection",
                    features: [
                      {
                        type: "Feature",
                        geometry: geojson,
                        properties: {}
                      }
                    ]
                  }}
                  isFormView={true}
                  mapId="form-map"
                  isCollapsed={isCollapsed}
                />
              </div>
            )
          }


          <div className="form-actions">
            {!viewOnly && !mapViewOnly && (
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  padding: "6px 16px",
                  textTransform: "none",
                }}
              >
                {loading ? (
                  <>
                    <FaSpinner className="icon-spin" />
                    {isEditMode ? " Updating..." : " Saving..."}
                  </>
                ) : (
                  isEditMode ? "Update" : "Save"
                )}
              </Button>
            )}

            {showEditPolygonButton && (
              <button
                type="button"
                onClick={() => {
                  const { site_id, ...formDataWithoutId } = formData;

                  console.log('✅ formData.geometry BEFORE saving:', formData.geometry);

                  if (typeof formData.geometry === 'string') {
                    console.log('⚠️ Geometry is in WKT format, converting to GeoJSON...');
                    const geojsonGeometry = wellknown.parse(formData.geometry);
                    console.log('✅ Converted GeoJSON:', geojsonGeometry);
                    localStorage.setItem('selectedPolygon', JSON.stringify(geojsonGeometry));
                    console.log('✅ Saved to localStorage:', JSON.stringify(geojsonGeometry));
                  } else if (formData.geometry) {
                    console.log('✅ Geometry is already in GeoJSON format.');
                    localStorage.setItem('selectedPolygon', JSON.stringify(formData.geometry));
                    console.log('✅ After saving, reading immediately:', localStorage.getItem('selectedPolygon'));
                    console.log('✅ Saved to localStorage:', JSON.stringify(formData.geometry));
                  } else {
                    console.error('❌ Geometry is missing. Cannot save.');
                    return;
                  }

                  localStorage.setItem('polygonArea', formData.area);
                  localStorage.setItem('siteFormData', JSON.stringify(formDataWithoutId));
                  localStorage.setItem('editingPolygonId', formData.site_id); // Save the current site ID
                  localStorage.setItem('editingPolygon', 'true');

                  console.log('✅ All localStorage values saved.');
                  setTimeout(() => navigate('/map-edit'), 100);
                }}
                className="btn-add-polygon"
              >
                Edit Polygon
              </button>

            )}


            {showAddPolygonButton && (
              <button
                type="button"
                onClick={() => {
                  const { site_id, ...formDataWithoutId } = formData;
                  localStorage.setItem('siteFormData', JSON.stringify(formDataWithoutId));
                  localStorage.setItem('fromMap', 'true'); // Set flag to indicate map navigation
                  window.location.href = '/map';
                }}
                className="btn-add-polygon"
              >
                Add Polygon
              </button>
            )}


            {/* Cancel button will be conditionally moved below */}
            {showCancelButton && (
              <button type="button" onClick={() => {
                localStorage.removeItem('siteFormData'); // Clear form data when cancel is clicked
                onClose();
              }} className="btn-cancel" disabled={loading}>
                Cancel
              </button>
            )}

            {showCancelButtonSitePage && (
              <button type="button" onClick={() => {
                localStorage.removeItem('siteFormData'); // Clear form data when cancel is clicked
                onClose();
              }} className="btn-cancel" disabled={loading}>
                Cancel
              </button>
            )}
          </div>
          {/* Move cancel button below for Site page only */}
          {/* {viewOnly && isSitePage &&( */}
          {
            showCloseButton && (
              <button type="button" onClick={onClose} className="cancel-below" disabled={loading}>
                Close
              </button>
            )
          }

        </form>
      </div >
    </div >
  );
};

export default CommonForm;
